import { PlusCircleOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { unstable_usePrompt } from 'react-router-dom';
import uuid from 'react-uuid';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Menu from '@/components/Menu/Menu';
import Typo from '@/components/Typography/Typo';
import {
  DASHBOARD_ROW_MAX_HEIGHT,
  DASHBOARD_ROW_MIN_HEIGHT,
  Dashboard2ColumnType,
  type Dashboard2DnDItemT,
} from '@/features/Dashboard/types';
import useColor from '@/hooks/domain/useColor';

import Button from '../../../../components/Button/Button';
import SimpleEditor from '../../../Dashboard/components/DashboardCardContent/SimpleEditor';
import { DashboardTextContentStyle } from '../../../Dashboard/components/styles/TextContentStyle';
import useReportStore from '../../store/ReportStore';
import type { ReportDnDColumnItemT, ReportDnDItemT, ReportRowT } from '../../types';

type Props = {
  rowId?: string;
  rowIndex: number;
  rows: ReportRowT[];
};

const allowColumnTypes = [
  String(Dashboard2ColumnType.DASHBOARD_COLUMN_CHART),
  String(Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX),
  String(Dashboard2ColumnType.DASHBOARD_COLUMN_ANALYSIS),
];

const ReportRowHandler = (props: Props) => {
  const initialTitleText = '<h1>Section Title</h1>';
  const initialDescriptionText = '<p>Section Description</p>';
  const color = useColor();
  const mode = useReportStore((state) => state.mode);

  const modifyRowHeight = useReportStore((state) => state.modifyRowHeight);
  const moveColumn = useReportStore((state) => state.moveColumn);
  const addRow = useReportStore((state) => state.addRow);
  const addItem = useReportStore((state) => state.addItem);
  const dragging = useReportStore((state) => state.dragging);
  const [isEditingTitle, setisEditingTitle] = useState(false);
  const [title, setTitle] = useState(initialTitleText);
  const [isResizing, setIsResizing] = useState<HTMLDivElement | null>(null);
  const startResizeY = useRef<number>(0);
  const startHeight = useRef<number>(0);
  const isReportEditMode = mode === 'EDIT';

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!props.rowId) return;
    const target = document.getElementById(props.rowId) as HTMLDivElement;
    if (!target) return;

    setIsResizing(target);
    startResizeY.current = e.clientY;
    startHeight.current = target.getBoundingClientRect().height;
    e.preventDefault();
  };

  const onDashboardDnDItemDrop = async (item: Dashboard2DnDItemT) => {
    const columnItem = item as ReportDnDColumnItemT;

    const row = props.rows[columnItem.rowIndex];

    const getPos = (): number => {
      // 위에서 아래로 보내는 경우 (같은 경우 포함)
      if (columnItem.rowIndex <= props.rowIndex) {
        if (row.columns.length === 1) {
          return props.rowIndex;
        } else {
          return props.rowIndex;
        }
      }
      return props.rowIndex;
    };

    const pos = getPos();
    const newRow = addRow(pos);

    moveColumn(columnItem.rowId, columnItem.column, newRow.alias, 0);
  };

  const [{ isOver }, dropZone] = useDrop<
    ReportDnDItemT,
    void,
    { isOver: boolean }
  >({
    accept: allowColumnTypes,
    collect(monitor) {
      const itemType = monitor.getItemType();
      if (typeof itemType !== 'string')
        return {
          isOver: false,
        };
      return {
        isOver: allowColumnTypes.includes(itemType) && monitor.isOver(),
      };
    },
    drop(item, monitor) {
      const type = monitor.getItemType();
      if (typeof type !== 'string') return;

      if (!allowColumnTypes.includes(type)) return;
      onDashboardDnDItemDrop(item);
    },
  });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const delta = e.clientY - startResizeY.current;
      const clamp = (value: number, min: number, max: number) =>
        Math.min(Math.max(value, min), max);
      const newHeight = clamp(
        Math.floor(startHeight.current + delta),
        DASHBOARD_ROW_MIN_HEIGHT,
        DASHBOARD_ROW_MAX_HEIGHT,
      );

      isResizing.style.height = `${newHeight}px`;
    };

    const handleMouseUp = () => {
      if (!props.rowId) return;
      if (!isResizing) return;
      const height = Math.floor(isResizing.getBoundingClientRect().height);
      modifyRowHeight(props.rowId, height);
      setIsResizing(null);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const createDashboardRowSection = async () => {
    if (title) {
      const row = addRow(props.rowIndex);
      addItem(
        {
          alias: uuid(),
          type: Dashboard2ColumnType.DASHBOARD_COLUMN_TEXT_BOX,
          text: title,
        },
        row.alias,
        0,
      );
      setisEditingTitle(false);
    }
  };

  useEffect(() => {
    if (!isReportEditMode) {
      setisEditingTitle(false);
    }
  }, [isReportEditMode]);

  unstable_usePrompt({
    when: isEditingTitle,
    message: 'Changes you made may not be saved.',
  });

  if (!isReportEditMode) return <HandlerStyle />;

  if (isEditingTitle) {
    return (
      <EditorWrapperStyle style={{ width: '100%' }} g={8}>
        <SimpleEditor
          value={title}
          onChange={setTitle}
          wrapperStyle={{
            backgroundColor: color.gray200,
            padding: '8px 24px',
            borderRadius: 16,
            height: '100%',
            maxHeight: 600,
            overflow: 'auto',
            flex: 1,
          }}
          textStyleCss={DashboardTextContentStyle}
        />
        <Flex j="space-between">
          <Flex></Flex>
          <Flex g={8} j="flex-end">
            <Button
              variant="secondary"
              onClick={() => setisEditingTitle(false)}
            >
              Cancel
            </Button>
            <Button onClick={createDashboardRowSection}>Done</Button>
          </Flex>
        </Flex>
      </EditorWrapperStyle>
    );
  }

  if (dragging)
    return (
      <Flex
        ref={dropZone}
        style={{
          height: '22px',
          width: '100%',
        }}
        a="center"
      >
        <Flex
          f={1}
          height="4px"
          style={{
            background: isOver ? color.primary200 : 'transparent',
          }}
        />
      </Flex>
    );

  return (
    <HandlerStyle
      g={8}
      a="center"
      ref={dropZone}
      onMouseDown={handleMouseDown}
      style={{
        cursor: 'row-resize',
      }}
    >
      <Menu
        options={[
          {
            label: 'Add Title',
            onClick: () => {
              setisEditingTitle(true);
              setTitle(initialTitleText);
            },
          },
          {
            label: 'Add Description',
            onClick: () => {
              setisEditingTitle(true);
              setTitle(initialDescriptionText);
            },
          },
        ]}
        triggerNode={
          <Flex
            g={6}
            a="center"
            style={{
              cursor: 'pointer',
              opacity: isOver ? 0 : 1,
            }}
          >
            <PlusCircleOutlined
              style={{
                color: color.primary200,
              }}
            />

            <Typo type="label2" weight="sb" color={color.primary200}>
              Add
            </Typo>
          </Flex>
        }
      />
      <Flex
        f={1}
        height="4px"
        style={{
          background: color.primary200,
        }}
      />
    </HandlerStyle>
  );
};

const HandlerStyle = styled(Flex)`
  opacity: 0;
  transition: opacity 0.3s;
  padding: 4px 0;

  height: 22px;
  user-select: none;

  &:hover {
    opacity: 1;
  }

  &:active {
    opacity: 1;
  }
`;

const EditorWrapperStyle = styled(VFlex)`
  flex: 1;
  flex-shrink: 1;
  min-height: 200px;
  overflow: auto;
  padding: 16px 0px;
`;

export default ReportRowHandler;
